<!--
Note: The EntityDialog and EntityMaskForm components can
be used to get data for both entity and subentity--for example,
for entity 'Company' and subentity 'CompanyContact' of 'Company'.
The two EntityDialog instances nest visually.
-->

<template>
  <v-row>
    <v-col>
      <!-- NO hard-coded z-index on the subentity dialog. Bridge pinned it to 9999
           and that was correct for Vuetify 2, whose Menuable mixin sized a menu
           from getMaxZIndex() — a live DOM scan — so a menu opened inside the
           dialog landed at 9999+2 and stayed on top. Vuetify 3 instead keeps a
           bookkeeping stack: useStack() (composables/stack.js) assigns each newly
           opened overlay `globalStack.at(-1) + 10`. An inline z-index does not
           update that stack, so the dialog PAINTED at 9999 while registering only
           ~2020, and the select menu opened inside it got ~2030 — i.e. under its
           own dialog. Letting the stack own the value keeps the whole chain
           ordered by construction: parent dialog 2000, its select menu 2010,
           subentity dialog 2020, that dialog's select menu 2030. -->
      <v-dialog
        v-model="propModel"
        :max-width="maxWidth"
        :overlay-opacity="overlayOpacity"
        persistent
        @keydown.esc="cancel"
      >
        <!-- `suppress-focus-on-first-field` was never bound here. The computed
             below, the store property and the whole
             setShowEntityDialog -> store -> dialog path for that flag all
             existed, but nothing handed it to the form, so every caller passing
             `suppressFocusOnFirstField` was silently ignored. (Only
             FieldSetFieldsMaskPreview / ...InOverlay worked, because they set
             the prop on EntityMaskForm directly.) -->
        <EntityMaskForm
          v-if="desiredAction !== 'DELETE'"
          :start-in-view-mode="startInViewMode"
          :disable-edit-button="disableEditButton"
          :suppress-focus-on-first-field="suppressFocusOnFirstField"
          :entity-name-title-override="
            isSubentityDialog
              ? subentityNameTitleOverride
              : entityNameTitleOverride
          "
          :entity-name-title-prefix-override="entityNameTitlePrefixOverride"
          :input-fields-entity-name-override="inputFieldsEntityNameOverride"
          :subtitle="subtitle"
          :message="message"
          :do-subdue-message="doSubdueMessage"
          :do-align-message-with-title="doAlignMessageWithTitle"
          :allow-add-another="allowAddAnother"
          :entity-name="entityName"
          :entity-data="entityData"
          :entity-parent-object-for-create="entityParentObjectForCreate"
          :save-button-caption-verb-override="saveButtonCaptionVerbOverride"
          :input-fields="inputFields"
          :max-width="maxWidth"
          :is-saving="isSaving"
          :is-subentity-dialog="isSubentityDialog"
          :values-for-subentity-fields-whose-values-to-set-from-entity-fields="
            valuesForSubentityFieldsWhoseValuesToSetFromEntityFields
          "
          :subentity-did-cancel="subentityDidCancel"
          :subentity-did-return-value-value="subentityDidReturnValueValue"
          @cancel="cancel"
          @save="saveEntityData"
        />
        <EntityDeleteForm
          v-if="desiredAction === 'DELETE'"
          :entity-name="entityName"
          :entity-name-title-override="entityNameTitleOverride"
          :entity-data="entityData"
          :delete-button-captions-verb-override="
            deleteButtonCaptionsVerbOverride
          "
          :max-width="maxWidth"
          :is-deleting="isDeleting"
          @cancel="cancel"
          @delete="callDeleteEntityData"
        />
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { cloneDeep } from 'lodash'
import Honeybadger from '@honeybadger-io/js'
import { isDarkTheme } from '~/cynqueModules/nuxtAccessors'
import type {
  TEntityId,
  TEntityData,
  TPartialEntityData,
  IEntityDataItem,
  TEntityMaskItem,
  IEntityMaskData,
  IEntityParentObjectForCreate,
} from '~/types/models'
import type { ISubentityAfterRequestCallback } from '~/cynqueModules/setShowEntityDialog'
import { assertIsDefined } from '~/types/models'
// Pure logic extracted from this component into unit-tested helpers (Nuxt 3 /
// Pinia migration prep — mirrors the TableCard / EntityMaskForm refactors). The
// computeds/methods below forward to these; see cynqueModules/componentHelpers/entityDialog/.
import {
  isEditingExistingData,
  shouldPatchOnSave,
  resolveTitlePrefix,
} from '~/cynqueModules/componentHelpers/entityDialog/dialogMode'
import {
  subentityDidCancel,
  resolveSubentityReturnValue,
} from '~/cynqueModules/componentHelpers/entityDialog/subentityReturn'
import {
  deriveEntityInstanceLabel,
  saveErrorNotification,
} from '~/cynqueModules/componentHelpers/entityDialog/saveFlow'
import { parseEntityMaskTitle } from '~/cynqueModules/componentHelpers/entityMaskForm/maskTitle'
import {
  callGetEntityInputFieldsService,
  setInputFieldsSelectLists,
  setInputFieldsStartAndValue,
} from '~/cynqueModules/getInputFields'
import {
  postEntityData,
  patchEntityData,
  deleteEntityData,
} from '~/cynqueModules/saveEntityData'
import {
  getCanSeeSplitFraction,
  SPLIT_FRACTION_IFIELD_NAME,
} from '~/cynqueModules/dataObjects/SplitFraction'
import { addNotification } from '~/cynqueModules/addNotification'
import { useEntityDialogStore } from '~/stores/entityDialog'

const mn = '[components/defaultLayout/EntityDialog.vue]'

export default defineComponent({
  props: {
    showEntityDialog: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    isSubentityDialog: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  data: () => ({
    inputFields: [] as unknown as IEntityMaskData,
    isSaving: false,
    isDeleting: false,
  }),
  computed: {
    propModel: {
      get(): boolean {
        return (
          this.showEntityDialog &&
          (this.hasInputFields || this.desiredAction === 'DELETE')
        )
      },
      set(value) {
        const fn = `${mn} set propModel |`
        console.debug(`${fn} value:`, value)
        if (value === false) {
          console.debug(
            `${fn} value is 'false'. Calling clearStoreValuesAndHideDialog()`
          )
          this.clearStoreValuesAndHideDialog()
        }
      },
    },
    startInViewMode(): boolean {
      return useEntityDialogStore().startInViewMode
    },
    disableEditButton(): boolean {
      return useEntityDialogStore().disableEditButton
    },
    suppressFocusOnFirstField(): boolean {
      return useEntityDialogStore().suppressFocusOnFirstField
    },
    maxWidth(): number {
      return this.isSubentityDialog ? 530 : 560
    },
    overlayOpacity(): number {
      return isDarkTheme() ? 0.8 : 0.5
    },
    entityDescriptor(): string {
      return this.isSubentityDialog ? 'subentity' : 'entity'
    },
    entityDescriptorCapitalized(): string {
      return (
        this.entityDescriptor.charAt(0).toUpperCase() +
        this.entityDescriptor.slice(1)
      )
    },
    entityNameDescriptor(): string {
      return this.entityDescriptor + 'Name'
    },
    entityName(): string | null {
      return useEntityDialogStore()[this.entityNameDescriptor]
    },
    entityData(): IEntityDataItem | undefined {
      return useEntityDialogStore().entityData
    },
    entityParentObjectForCreate(): IEntityParentObjectForCreate | undefined {
      return useEntityDialogStore().entityParentObjectForCreate
    },
    entityId(): TEntityId {
      return useEntityDialogStore().entityId
    },
    entityNameTitlePrefixOverride(): string | null | undefined {
      return resolveTitlePrefix({
        override: useEntityDialogStore().entityNameTitlePrefixOverride,
        entityData: this.entityData,
        isSubentityDialog: this.isSubentityDialog,
      })
    },
    entityNameTitleOverride(): string | undefined {
      return useEntityDialogStore().entityNameTitleOverride
    },
    /**
     * What to CALL this entity when talking to the user.
     *
     * Mirrors the title binding this dialog already passes to EntityMaskForm,
     * so a failure message says "Opportunity Service Group Team Member" — the
     * label the store already holds — rather than the raw `entityName`
     * identifier. A user really did read "Couldn't create
     * opportunityMSTeamMember" (Honeybadger 133565364). parseEntityMaskTitle
     * strips the '<br/>' and '^^^' encodings and falls back to the capitalized
     * entity name, so this is never empty.
     */
    entityLabelForUser(): string {
      const override = this.isSubentityDialog
        ? this.subentityNameTitleOverride
        : this.entityNameTitleOverride
      const parsed = parseEntityMaskTitle(override, this.entityName)
      return parsed.title || parsed.entityNameCapitalized || 'record'
    },
    inputFieldsEntityNameOverrideDescriptor(): string {
      return this.isSubentityDialog ? 'subentityI' : 'i'
    },
    inputFieldsEntityNameOverride(): string | undefined {
      const fn = `${mn} inputFieldsEntityNameOverride (computed) |`
      const override =
        useEntityDialogStore()[
          `${this.inputFieldsEntityNameOverrideDescriptor}nputFieldsEntityNameOverride`
        ]
      console.debug(`${fn} returning`, override)
      return override
    },
    inputFieldsOverride(): TEntityMaskItem[] | undefined {
      return useEntityDialogStore().inputFieldsOverride
    },
    subtitle(): string | undefined {
      return useEntityDialogStore().subtitle
    },
    message(): string | undefined {
      return useEntityDialogStore().message
    },
    doSubdueMessage(): boolean {
      return useEntityDialogStore().doSubdueMessage
    },
    doAlignMessageWithTitle(): boolean {
      return useEntityDialogStore().doAlignMessageWithTitle
    },
    storeEditedInputDataAsJsonInProperty(): string | undefined {
      return useEntityDialogStore().storeEditedInputDataAsJsonInProperty
    },
    saveButtonCaptionVerbOverride(): string | undefined {
      return useEntityDialogStore().saveButtonCaptionVerbOverride
    },
    deleteButtonCaptionsVerbOverride(): string | undefined {
      return useEntityDialogStore().deleteButtonCaptionsVerbOverride
    },
    allowAddAnother(): boolean {
      return useEntityDialogStore().allowAddAnother && !this.isSubentityDialog
    },
    desiredAction(): string | undefined {
      return useEntityDialogStore().desiredAction
    },
    preRequestProcessor(): Function | undefined {
      return useEntityDialogStore().preRequestProcessor
    },
    afterRequestCallback(): Function {
      return useEntityDialogStore().afterRequestCallback
    },
    subentityReturnValue(): any {
      return useEntityDialogStore().subentityReturnValue
    },
    subentityNameTitleOverride(): string | undefined {
      return useEntityDialogStore().subentityNameTitleOverride
    },
    subentityAfterRequestProcessor(): Function | undefined {
      return useEntityDialogStore().subentityAfterRequestProcessor
    },
    subentityAfterRequestCallbacks():
      | ISubentityAfterRequestCallback[]
      | undefined {
      return useEntityDialogStore().subentityAfterRequestCallbacks
    },
    subentityParentObjectForCreate(): IEntityParentObjectForCreate | undefined {
      return useEntityDialogStore().subentityParentObjectForCreate
    },
    valuesForSubentityFieldsWhoseValuesToSetFromEntityFields():
      | string[]
      | undefined {
      return useEntityDialogStore()
        .valuesForSubentityFieldsWhoseValuesToSetFromEntityFields
    },
    subentityDidCancel(): boolean {
      return subentityDidCancel({
        subentityReturnValue: this.subentityReturnValue,
        isSubentityDialog: this.isSubentityDialog,
      })
    },
    subentityDidReturnValueValue(): any {
      return resolveSubentityReturnValue({
        subentityReturnValue: this.subentityReturnValue,
        isSubentityDialog: this.isSubentityDialog,
      })
    },
    hasInputFields(): boolean {
      const fn = `${mn} hasInputFields |`
      const hasIFields = this.inputFields && this.inputFields.length > 0
      console.debug(`${fn} return value: ${hasIFields}`)
      return hasIFields
    },
    editingExistingData(): boolean {
      return isEditingExistingData({
        entityData: this.entityData,
        desiredAction: this.desiredAction,
      })
    },
  },
  watch: {
    showEntityDialog(value, oldValue) {
      console.debug(
        `${mn} watch showEntityDialog | changed from '${oldValue}' to '${value}'`
      )
      if (value) {
        if (this.desiredAction === 'DELETE') {
          return
        }
        this.getInputFields()
      }
    },
    subentityReturnValue(value) {
      const fn = `${mn} watch subentityReturnValue |`
      if (this.isSubentityDialog) {
        return
      }
      console.debug(`${fn} changed to`, value)
      console.debug(`${fn} subentityDidCancel: ${this.subentityDidCancel}`)
      console.debug(
        `${fn} subentityDidReturnValueValue:`,
        this.subentityDidReturnValueValue
      )
    },
  },
  methods: {
    async getInputFields() {
      const fn = `${mn} getInputFields |`
      console.debug(`${fn}`)
      if (!this.entityName) {
        throw new Error(`entityName is falsey`)
      }
      try {
        const inputFields = await callGetEntityInputFieldsService({
          mn: fn,
          inputFieldsOverride: this.inputFieldsOverride,
          inputFieldsEntityNameOverride: this.inputFieldsEntityNameOverride,
          entityName: this.entityName,
          isSubentityDialog: this.isSubentityDialog,
        })
        //
        if (!getCanSeeSplitFraction({ callingFn: fn })) {
          this.unrenderSplitFractionInputField({ inputFields })
        }
        //
        if (!this.isSubentityDialog && this.editingExistingData) {
          setInputFieldsStartAndValue({
            mn: fn,
            entityData: this.entityData,
            inputFields,
          })
        }
        // Assign this.inputFields FIRST, then load the select lists against the reactive
        // copy. The field bodies call setItemsForSelect fire-and-forget, so its
        // `targetField.items = ...` writes land asynchronously — after this point. In
        // Vue 2 (Object.defineProperty) that write on the original object was tracked; in
        // Vue 3 only mutations THROUGH the reactive proxy trigger a re-render, so loading
        // against the pre-reactive array left pre-populated selects blank until focus.
        // Loading against this.inputFields makes those late writes reactive.
        // (setInputFieldsSelectLists runs even for creates so pre-populated defaults show.)
        console.debug(`${mn} getInputFields | setting this.inputFields`)
        this.inputFields = inputFields
        await setInputFieldsSelectLists({
          mn: fn,
          inputFields: this.inputFields,
          entityParentObjectForCreate: this.entityParentObjectForCreate,
        })
      } catch (e) {
        console.error(`${fn} error -`, e)
        addNotification({
          type: 'warning',
          message: `Couldn't get ${this.entityName} input fields`,
        })
        this.close()
      }
    },
    unrenderSplitFractionInputField({
      inputFields,
    }: {
      inputFields: IEntityMaskData[]
    }) {
      const index = inputFields.findIndex(
        (ifield: IEntityMaskData) => ifield.name === SPLIT_FRACTION_IFIELD_NAME
      )
      if (index !== -1) {
        inputFields[index].unrendered = true
      }
    },
    saveEntityData({
      newOrUpdatedEntityData,
      wasClickedSaveAndAddAnother,
    }: {
      newOrUpdatedEntityData: any
      wasClickedSaveAndAddAnother: boolean
    }) {
      const fn = `${mn} saveEntityData |`
      if (
        shouldPatchOnSave({
          entityData: this.entityData,
          isSubentityDialog: this.isSubentityDialog,
        })
      ) {
        console.debug(
          `${fn} this.entityData is NOT null. Calling this.patchEntityData.`
        )
        this.callPatchEntityData({ newOrUpdatedEntityData })
      } else {
        console.debug(
          `${fn} this.entityData IS null (no pre-existing instance). Calling this.postEntityData.`
        )
        this.callPostEntityData({
          newOrUpdatedEntityData,
          wasClickedSaveAndAddAnother,
        })
      }
    },
    async callPostEntityData({
      newOrUpdatedEntityData,
      wasClickedSaveAndAddAnother,
    }: {
      newOrUpdatedEntityData: any
      wasClickedSaveAndAddAnother: boolean
    }) {
      const fn = `${mn} callPostEntityData |`
      try {
        this.isSaving = true
        assertIsDefined(this.entityName, fn) // keep Typescript happy
        await postEntityData({
          entityName: this.entityName,
          newEntityData: newOrUpdatedEntityData,
          entityParentObjectForCreate: this.entityParentObjectForCreate,
          preRequestProcessor: this.preRequestProcessor,
          afterRequestCallback: this.afterRequestCallback,
          isSubentityDialog: this.isSubentityDialog,
          subentityAfterRequestProcessor: this.subentityAfterRequestProcessor,
          subentityAfterRequestCallbacks: this.subentityAfterRequestCallbacks,
          subentityParentObjectForCreate: this.subentityParentObjectForCreate,
        })
        const entityInstanceNameForSnack = deriveEntityInstanceLabel(
          newOrUpdatedEntityData
        )
        // @ts-expect-error
        this.$setSnack({
          callingMn: mn,
          snack: {
            message: `New ${this.entityName}${entityInstanceNameForSnack} created successfully.`,
            timeout: 2500,
          },
        })
      } catch (e: any) {
        // The create path is the only one of the three that was not logging.
        // `preRequestProcessor` (e.g. the msServicesCard "…based on Product"
        // builders) runs inside `postEntityData`, so a TypeError thrown deep in
        // the build chain surfaced ONLY as a snackbar carrying `e.message` --
        // no stack anywhere in the console. Match callPatchEntityData /
        // callDeleteEntityData.
        console.error(`${fn} error -`, e)
        Honeybadger.notify(e)
        addNotification(this.notificationForSaveError(e, 'create'))
      } finally {
        this.isSaving = false
        if (this.allowAddAnother && wasClickedSaveAndAddAnother) {
          this.clearLocalInputFieldValues()
          return
        }
        this.close()
      }
    },
    /**
     * Build the user-facing notification for a failed save.
     *
     * `postEntityData`/`patchEntityData`/`deleteEntityData` surface very
     * different failures through the same `catch`: the write itself failing, the
     * API rejecting the payload, and the write SUCCEEDING but
     * `afterRequestCallback` throwing afterwards. Rendering all of them as
     * "Couldn't update <entity>" is a lie in the last case — the change is on
     * the server — and users then re-enter work that was never lost.
     *
     * The branching lives in a pure, tested module; this forwards to it with the
     * user-facing label. Reporting to Honeybadger is unchanged.
     */
    notificationForSaveError(e: unknown, verb: 'create' | 'update' | 'delete') {
      return saveErrorNotification({
        error: e,
        verb,
        entityLabel: this.entityLabelForUser,
      })
    },
    async callPatchEntityData({
      newOrUpdatedEntityData,
    }: {
      newOrUpdatedEntityData: TPartialEntityData
    }) {
      const fn = `${mn} callPatchEntityData |`
      assertIsDefined(this.entityName, fn)
      try {
        this.isSaving = true
        await patchEntityData({
          entityName: this.entityName,
          entityId: this.entityId,
          entityData: this.storeEditedInputDataAsJsonInProperty
            ? this.entityData
            : undefined,
          updatedEntityData: newOrUpdatedEntityData,
          preRequestProcessor: this.preRequestProcessor,
          afterRequestCallback: this.afterRequestCallback,
          storeEditedInputDataAsJsonInProperty:
            this.storeEditedInputDataAsJsonInProperty,
        })
      } catch (e) {
        console.error(`${fn} error -`, e)
        Honeybadger.notify(e)
        addNotification(this.notificationForSaveError(e, 'update'))
      } finally {
        this.isSaving = false
        this.close()
      }
    },
    async callDeleteEntityData(entityDataToDelete: TEntityData) {
      const fn = `${mn} callDeleteEntityData |`
      try {
        this.isDeleting = true
        // `await`, not a bare call. Without it this `try` never saw the
        // rejection, so the catch below was dead code: a failed delete (409,
        // 422, a network error) told the user NOTHING, `finally` closed the
        // dialog as though it had succeeded while `afterRequestCallback` never
        // ran so the row stayed put, and the rejection escaped to the global
        // handler as an UnhandledPromiseRejectionWarning (Honeybadger 133820844).
        // It also let `isDeleting` flip true->false within one tick, so the
        // button never disabled or showed its loading state. callPostEntityData
        // and callPatchEntityData already awaited and asserted; only this one
        // did neither.
        assertIsDefined(this.entityName, fn)
        await deleteEntityData({
          entityName: this.entityName,
          entityDataToDelete,
          afterRequestCallback: this.afterRequestCallback,
        })
      } catch (e) {
        console.error(`${fn} error -`, e)
        Honeybadger.notify(e)
        addNotification(this.notificationForSaveError(e, 'delete'))
      } finally {
        this.isDeleting = false
        this.close()
      }
    },
    clearLocalInputFieldValues() {
      this.inputFields.forEach(
        (inputField: IEntityMaskData) => (inputField.value = null)
      )
      this.inputFields = cloneDeep(this.inputFields) // Trick to force reactivity for nested data
    },
    cancel() {
      const fn = `${mn} cancel |`
      // Esc reaches here through the v-dialog's `@keydown.esc` even while a
      // request is in flight; the Cancel buttons are disabled then, Esc was not.
      // Closing mid-save let the user navigate away before the response, so the
      // afterRequestCallback ran against the page they had left (Honeybadger
      // 135148166). The request cannot be cancelled anyway -- wait for it.
      if (this.isSaving || this.isDeleting) {
        console.debug(`${fn} ignored -- a save or delete is in flight`)
        return
      }
      if (this.isSubentityDialog) {
        console.debug(
          `${fn} calling store action` +
            ` 'setSubentityReturnValue' and setting 'CANCELLED' `
        )
        useEntityDialogStore().setSubentityReturnValue('CANCELLED')
      }
      this.close()
    },
    close() {
      this.propModel = false
    },
    async clearStoreValuesAndHideDialog() {
      const fn = `${mn} clearStoreValuesAndHideDialog |`
      const functionName = `clear${this.entityDescriptorCapitalized}Dialog`
      console.debug(`${fn} calling ${functionName}`)
      await useEntityDialogStore()[functionName]()
      this.inputFields = []
    },
  },
})
</script>

<style scoped>
:deep(.v-dialog) {
  overflow-x: hidden;
}
</style>
